/**
 * VideoDisplay Component
 * Renders video element with stream and handles video element lifecycle
 * 
 * Requirement: 2.1
 */

import { useEffect, useRef } from 'react';
import './VideoDisplay.css';

interface VideoDisplayProps {
  stream: MediaStream | null;
  onVideoReady?: (videoElement: HTMLVideoElement) => void;
  className?: string;
}

export const VideoDisplay: React.FC<VideoDisplayProps> = ({
  stream,
  onVideoReady,
  className = '',
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamIdRef = useRef<string | null>(null);
  const hasNotifiedReadyRef = useRef(false);

  useEffect(() => {
    console.log('[VideoDisplay] useEffect triggered, stream:', stream?.id);
    const videoElement = videoRef.current;
    if (!videoElement) {
      console.log('[VideoDisplay] No video element ref');
      return;
    }

    // Set up video element
    if (stream) {
      // Only set srcObject if it's a different stream to prevent flickering
      const currentStreamId = (videoElement.srcObject as MediaStream)?.id;
      const newStreamId = stream.id;
      
      console.log('[VideoDisplay] Current stream ID:', currentStreamId);
      console.log('[VideoDisplay] New stream ID:', newStreamId);
      
      if (currentStreamId === newStreamId && streamIdRef.current === newStreamId) {
        console.log('[VideoDisplay] Same stream, skipping update');
        return;
      }
      
      console.log('[VideoDisplay] Setting new stream to video element');
      streamIdRef.current = newStreamId;
      hasNotifiedReadyRef.current = false; // Reset ready flag for new stream
      videoElement.srcObject = stream;
      
      // Notify parent when video is ready
      const handleLoadedMetadata = async () => {
        console.log('[VideoDisplay] Video metadata loaded, attempting to play...');
        try {
          // iOS-specific: Ensure video is ready before playing
          if (videoElement.readyState >= 2) {
            await videoElement.play();
            console.log('[VideoDisplay] Video playing successfully');
          } else {
            console.log('[VideoDisplay] Video not ready, waiting...');
            // Wait for video to be ready
            await new Promise((resolve) => {
              const checkReady = () => {
                if (videoElement.readyState >= 2) {
                  resolve(void 0);
                } else {
                  setTimeout(checkReady, 100);
                }
              };
              checkReady();
            });
            await videoElement.play();
            console.log('[VideoDisplay] Video playing after wait');
          }
        } catch (err) {
          console.error('[VideoDisplay] Failed to play video:', err);
          // iOS-specific: Try to recover from play failure
          if (/iPhone|iPad/.test(navigator.userAgent)) {
            console.log('[VideoDisplay] iOS play failure, attempting recovery...');
            try {
              // Reset and try again
              videoElement.load();
              await new Promise(resolve => setTimeout(resolve, 500));
              await videoElement.play();
              console.log('[VideoDisplay] iOS recovery successful');
            } catch (recoveryErr) {
              console.error('[VideoDisplay] iOS recovery failed:', recoveryErr);
            }
          }
        }
        
        // Only notify once per stream
        if (onVideoReady && !hasNotifiedReadyRef.current) {
          console.log('[VideoDisplay] Notifying parent video is ready');
          hasNotifiedReadyRef.current = true;
          onVideoReady(videoElement);
        } else {
          console.log('[VideoDisplay] Skipping ready notification (already notified)');
        }
      };

      // iOS-specific: Handle play interruption
      const handlePlayInterruption = () => {
        console.log('[VideoDisplay] Video play interrupted, attempting to resume...');
        if (videoElement.paused && stream && stream.active) {
          videoElement.play().catch(err => {
            console.error('[VideoDisplay] Failed to resume video:', err);
          });
        }
      };

      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.addEventListener('pause', handlePlayInterruption);

      return () => {
        console.log('[VideoDisplay] Cleanup called');
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        videoElement.removeEventListener('pause', handlePlayInterruption);
      };
    } else {
      console.log('[VideoDisplay] No stream, clearing srcObject');
      videoElement.srcObject = null;
      streamIdRef.current = null;
      hasNotifiedReadyRef.current = false;
    }
  }, [stream, onVideoReady]);

  return (
    <div className={`video-display-container ${className}`}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="video-display"
      />
      {!stream && (
        <div className="video-placeholder">
          <p>Camera not active</p>
        </div>
      )}
    </div>
  );
};

export default VideoDisplay;
